import { useState, useEffect } from 'react';
import { getUserCountryCode } from '../utils/getusercountrycode';
import { fetchLivePrice, fetchLivePrices } from '../utils/fetchliveprice';

// Shopify MoneyV2 amounts are decimal dollars (e.g. "8.45").
const toPriceAmount = (amount) => {
  const value = parseFloat(amount);
  return Number.isNaN(value) ? 0 : value;
};
// Custom hook for fetching and updating product prices based on geolocation (all Products)
const useUpdatedProductPrices = (allShopifyProducts) => {

  const initialProducts = allShopifyProducts?.edges || [];
  const [products, setProducts] = useState(initialProducts);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updatePrices = async () => {
      setLoading(true);

      try {
        const cleanProducts = allShopifyProducts?.edges?.filter(({ node }) => node && node.handle);
        const handles = cleanProducts?.map(({ node }) => node.handle);

        const countryCode = await getUserCountryCode();
        const updated = await fetchLivePrices(handles, countryCode);


        const merged = cleanProducts.map(({ node }) => {
          const override = updated.filter((p) => p?.handle === node.handle)?.map(prod => ({
            ...prod,
            priceRange: {
              minVariantPrice: {
                amount: toPriceAmount(prod?.priceRange?.minVariantPrice?.amount),
                currencyCode: prod?.priceRange?.minVariantPrice?.currencyCode,
              },
              maxVariantPrice: {
                amount: toPriceAmount(prod?.priceRange?.maxVariantPrice?.amount),
                currencyCode: prod?.priceRange?.maxVariantPrice?.currencyCode
              }
            }
          }))[0]

          const normalizedPriceRange = override?.priceRange || {
            minVariantPrice: {
              amount: toPriceAmount(node?.priceRange?.minVariantPrice?.amount),
              currencyCode: node?.priceRange?.minVariantPrice?.currencyCode,
            },
            maxVariantPrice: {
              amount: toPriceAmount(node?.priceRange?.maxVariantPrice?.amount),
              currencyCode: node?.priceRange?.maxVariantPrice?.currencyCode
            }
          };


          return {
            node: {
              ...node,
              priceRange: normalizedPriceRange,
            },
          };
        });

        setProducts(merged);
      } catch (err) {
        console.error("Failed to update prices:", err);
      } finally {
        setLoading(false);
      }
    };

    updatePrices();
  }, [typeof window !== 'undefined' ? allShopifyProducts.edges : null]);

  return { products, loading };
};
// Custom hook for fetching and updating product prices based on geolocation (single Products)
// Custom hook for a single product's live geolocation-based price
export const useSingleUpdatedProductPrice = (handle, initialPriceRange) => {
  const [priceRange, setPriceRange] = useState(initialPriceRange);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !handle) return;

    const updatePrice = async () => {
      setLoading(true);
      try {
        const countryCode = await getUserCountryCode();
        const updated = await fetchLivePrice(handle, countryCode);
        if (updated?.handle === handle) {
          setPriceRange(updated.priceRange);
        }
      } catch (err) {
        console.error("Failed to fetch single product price:", err);
      } finally {
        setLoading(false);
      }
    };

    updatePrice();
  }, [handle]);

  return { priceRange, loading };
};


export default useUpdatedProductPrices;
